class ScrollPreserver {
  private static readonly STORAGE_KEY = 'scrollPosition';
  private static readonly TRIGGER_CLASS = 'preserve-scroll';
  private static readonly SCROLL_TIMEOUT = 100;

  private static instance: ScrollPreserver;
  private scrollRestored: boolean = false;

  private constructor() {
    this.initialize();
  }

  public static init(): void {
    if (!this.instance) {
      this.instance = new ScrollPreserver();
    }
  }

  private initialize(): void {
    // Check if the trigger class is present on page load
    if (document.body.classList.contains(ScrollPreserver.TRIGGER_CLASS)) {
      this.setupScrollPreservation();
    } else {
      sessionStorage.removeItem(ScrollPreserver.STORAGE_KEY);
    }

    // Set up a MutationObserver to watch for class changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          if (target.classList.contains(ScrollPreserver.TRIGGER_CLASS)) {
            this.setupScrollPreservation();
          } else {
            this.teardownScrollPreservation();
          }
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  private setupScrollPreservation(): void {
    // Save scroll position before unload
    window.addEventListener('beforeunload', this.saveScrollPosition);

    // Restore scroll position after load
    if (!this.scrollRestored) {
      this.restoreScrollPosition();
    }

    // Set up form submission handlers
    this.setupFormHandlers();
  }

  private teardownScrollPreservation(): void {
    window.removeEventListener('beforeunload', this.saveScrollPosition);

    // Remove form handlers
    const forms = document.querySelectorAll('form');
    forms.forEach(form => {
      form.removeEventListener('submit', this.saveScrollPosition);
    });

  }

  private saveScrollPosition = (): void => {
    sessionStorage.setItem(ScrollPreserver.STORAGE_KEY, window.scrollY.toString());
  };

  private restoreScrollPosition(): void {
    const scrollPosition = sessionStorage.getItem(ScrollPreserver.STORAGE_KEY);
    if (scrollPosition) {
      setTimeout(() => {
        window.scrollTo({ top: parseInt(scrollPosition, 10), left: 0, behavior: "smooth" });
        sessionStorage.removeItem(ScrollPreserver.STORAGE_KEY);
        this.scrollRestored = true;
      }, ScrollPreserver.SCROLL_TIMEOUT);
    }
  }

  private setupFormHandlers(): void {
    const forms = document.querySelectorAll('form');
    forms.forEach(form => {
      form.addEventListener('submit', this.saveScrollPosition);
    });
  }
}

// Initialize the scroll preservation when the DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ScrollPreserver.init());
} else {
  ScrollPreserver.init();
}
